// @ts-strict-ignore
import { Matrix4, Mesh, Vector3 } from 'three';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { Line } from '@3d-scene/model/line';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Dictionary } from 'typescript-collections';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { ArrayUtils } from '@shared/util/array-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ConnectionFactory } from '@3d-scene/model3d/connection.factory';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';
import { ReplaceBusbarUtils } from './replace/busbar/replace-busbar-utils';
import { StringUtils } from '@shared/util/string-utils';

export class InsertArticleHelper {
  private _acceptingAreaUsage: ConnectionAreaUsageVO;
  private _acceptingConnection: Connection;
  private _acceptingIdentity: MetaElementUsageIdentityVO;
  private _acceptingTransform: Matrix4;
  private _acceptingArticleType: string;
  private _baseLine: AdvancedLine;
  private _configuredLine: AdvancedLine;

  constructor(
    acceptingContainer: ArticleContainer,
    metaElementName: string,
    private containerService: ContainerService,
    private connectionFactory: ConnectionFactory,
    private insertArticleUtils: InsertArticleUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private articleLineHolder: ArticleLineHolder
  ) {
    this._acceptingAreaUsage = acceptingContainer.findMetaElementUsageByName(metaElementName) as ConnectionAreaUsageVO;
    this._acceptingConnection = acceptingContainer.findConnectionByName(metaElementName);
    this._acceptingArticleType = acceptingContainer.article.type.displayName;
    this._acceptingIdentity = MetaElementUsageIdentityVO.create(acceptingContainer.articleGUID, metaElementName);
    if (this._acceptingConnection) {
      this._acceptingTransform = acceptingContainer.matrix.clone();
      this._baseLine = containerService.calculateBaseLine(this._acceptingConnection, this._acceptingTransform, true);
    }
  }

  public get acceptingArticleUsageId(): string {
    return !this._acceptingIdentity ? null : this._acceptingIdentity.articleUsageId;
  }

  public get acceptingIdentity(): MetaElementUsageIdentityVO {
    return this._acceptingIdentity;
  }

  public get acceptingArticleType(): string {
    return this._acceptingArticleType;
  }

  public get acceptingConnection(): Connection {
    return this._acceptingConnection;
  }

  public get baseLine(): AdvancedLine {
    return this._baseLine;
  }

  public get configuredLine(): AdvancedLine {
    if (!this._configuredLine) {
      this._configuredLine = this.calculateConfiguredLine();
    }
    return this._configuredLine;
  }

  public get startPoint(): Vector3 {
    return !this._baseLine ? null : this._baseLine.start;
  }

  public get endPoint(): Vector3 {
    return !this._baseLine ? null : this._baseLine.end;
  }

  private calculateConfiguredLine(): AdvancedLine {
    if (!this._acceptingAreaUsage) {
      return this._baseLine;
    }
    let freeLines: AdvancedLine[] = [this._baseLine];
    const connectedContainers: ArticleContainer[] = this.getConnectedArticlesByAcceptingArea(this._acceptingAreaUsage);
    let firstArticleLine: Line = null;
    for (const articleContainer of connectedContainers) {
      if (!firstArticleLine) {
        firstArticleLine = this.getArticleLine(articleContainer);
      }

      if (this.isArticleConfigured(articleContainer.article)) {
        continue;
      }

      const articleLine: AdvancedLine = this.getArticleLine(articleContainer);
      if (articleLine) {
        freeLines = this.containerService.splitLines(freeLines, articleLine);
      }
    }
    if (freeLines.length === 0) {
      return new AdvancedLine(firstArticleLine.end, firstArticleLine.end);
    } else {
      const startLine: Line = freeLines[0];
      const endLine: Line = freeLines[freeLines.length - 1];
      return new AdvancedLine(startLine.start, endLine.end);
    }
  }

  /**
   * Checks if its available to relocate article with given GUID to new position and new length on the mounting rail.
   */
  public isRelocationAvailable(articleGUID: string, newOffset: number, newLength: number, checkBaseLineBounds: boolean = true): boolean {
    return this.isInsertOperationAvailable(newOffset, newLength, [articleGUID], checkBaseLineBounds);
  }

  /**
   * Method moves current articles on rail to insert new article with the given line in the free space.
   *
   * @return dictionary (key : articleGUID, value : move offset) if there is enough free space to insert the article, otherwise null
   */
  public moveArticlesToCreateFreeSpace(
    articleLine: AdvancedLine,
    excludedArticleGUIDs: string[] = null,
    checkBaseLineBounds: boolean = true,
    removeGap: boolean = false,
    moveLeft: boolean = false,
    forceUnmoveableArticles: boolean = false,
    ignoreBaseLineOutOfBoundsCheck: boolean = false
  ): Dictionary<string, Vector3> {
    return this.calculateMoveOffsetByArticleLine(
      articleLine,
      null,
      excludedArticleGUIDs,
      checkBaseLineBounds,
      true,
      removeGap,
      moveLeft,
      forceUnmoveableArticles,
      ignoreBaseLineOutOfBoundsCheck
    );
  }

  public replaceArticle(
    replaceArticleLine: AdvancedLine,
    articleLine: AdvancedLine,
    excludedArticleGUIDs: string[] = null,
    moveAfterCheck: boolean = true,
    maxAllowedMoveOffsetMap: Dictionary<string, number> = null
  ): Dictionary<string, Vector3> {
    if (!replaceArticleLine || !articleLine) {
      return null;
    }

    if (!this._baseLine.hasLine(articleLine)) {
      return null;
    }

    const overlapInfo: OverlapInfo = new OverlapInfo(null, replaceArticleLine.overlapRight, NaN, replaceArticleLine.overlapRightTypes);
    const commonLine: AdvancedLine = new AdvancedLine(replaceArticleLine.end, this._baseLine.end, overlapInfo);
    const sortedArticlesOnRail: ArticleContainer[] = this.getConnectedArticlesFromLine(commonLine, excludedArticleGUIDs);
    let removeGap: boolean = false;
    if (sortedArticlesOnRail.length > 0) {
      const neighborArticleLine: Line = this.getArticleLine(sortedArticlesOnRail[0]);
      removeGap = MathUtils.equalsNumber3D(replaceArticleLine.end, neighborArticleLine.start);
    }
    const moveMap: Dictionary<string, Vector3> = this.calculateOffsetToMoveArticles(
      sortedArticlesOnRail,
      articleLine,
      removeGap,
      false,
      false,
      false,
      false,
      excludedArticleGUIDs,
      maxAllowedMoveOffsetMap
    );
    if (moveMap && moveAfterCheck) {
      this.insertArticleUtils.moveArticles(sortedArticlesOnRail, moveMap);
    }
    return moveMap;
  }

  public isInsertOperationAvailable(
    offset: number,
    length: number,
    excludedArticleGUIDs: string[] = null,
    checkBaseLineBounds: boolean = true
  ): boolean {
    const articleLine: AdvancedLine = this.calculateArticleLine(offset, length);
    const moveArticlesMap: Dictionary<string, Vector3> = this.calculateMoveOffsetByArticleLine(
      articleLine,
      null,
      excludedArticleGUIDs,
      checkBaseLineBounds
    );
    return !moveArticlesMap ? false : true;
  }

  public calculateMoveOffsetByArticleLine(
    articleLine: AdvancedLine,
    excludedCombinedArticleGuids: string[] = null,
    excludedOnRailArticleGUIDs: string[] = null,
    checkBaseLineBounds: boolean = true,
    moveAfterCheck: boolean = false,
    removeGap: boolean = false,
    moveLeft: boolean = false,
    forceUnmoveableArticles: boolean = false,
    ignoreBaseLineOutOfBoundsCheck: boolean = false,
    removeAllGaps: boolean = false
  ): Dictionary<string, Vector3> {
    if (checkBaseLineBounds && !this._baseLine.hasLine(articleLine)) {
      return null;
    }
    const commonLineStart: Vector3 = moveLeft ? this.baseLine.start : articleLine.start;
    const commonLineEnd: Vector3 = moveLeft ? articleLine.end : this.baseLine.end;
    const commonOverlap: OverlapInfo = moveLeft
      ? articleLine.overlapInfo.copyOverlapRightOnly()
      : articleLine.overlapInfo.copyOverlapLeftOnly();
    const commonLine: AdvancedLine = new AdvancedLine(commonLineStart, commonLineEnd, commonOverlap);

    const sortedArticlesOnRail: ArticleContainer[] = this.getConnectedArticlesFromLine(commonLine, excludedOnRailArticleGUIDs, false, moveLeft);
    const moveMap: Dictionary<string, Vector3> = this.calculateOffsetToMoveArticles(
      sortedArticlesOnRail,
      articleLine,
      removeGap,
      moveLeft,
      forceUnmoveableArticles,
      ignoreBaseLineOutOfBoundsCheck,
      removeAllGaps,
      excludedCombinedArticleGuids
    );
    if (moveMap && moveAfterCheck) {
      this.insertArticleUtils.moveArticles(sortedArticlesOnRail, moveMap);
    }
    return moveMap;
  }

  /**
   * Searches articles placed on the base line between given start and end article containers.
   */
  public getConnectedArticlesBetweenContainers(
    startArticle: ArticleContainer,
    endArticle: ArticleContainer,
    excludedArticleGUIDs: string[] | null = null,
    considerOnlyConfiguredArticles: boolean = false
  ): ArticleContainer[] {
    const commonLine: AdvancedLine = this.calculateCommonArticlesLine([startArticle, endArticle]);
    return this.getConnectedArticlesFromLine(commonLine, excludedArticleGUIDs, considerOnlyConfiguredArticles);
  }

  /**
   * Searches articles placed on the base line between given start and end points.
   */
  public getConnectedArticlesFromLine(
    commonLine: AdvancedLine,
    excludedArticleGUIDs: string[] = null,
    considerOnlyConfiguredArticles: boolean = false,
    reverseSort: boolean = false
  ): ArticleContainer[] {
    const allConnectedArticles: ArticleContainer[] = this.getConnectedArticlesByAcceptingArea(
      this._acceptingAreaUsage,
      considerOnlyConfiguredArticles
    );
    const articlesBetweenPoints: ArticleContainer[] = this.findArticlesFromLine(commonLine, allConnectedArticles, excludedArticleGUIDs);

    return this.sortArticlesByLine(articlesBetweenPoints, this._baseLine, reverseSort);
  }

  public sortArticles(articlesOnRail: ArticleContainer[]): ArticleContainer[] {
    return this.sortArticlesByLine(articlesOnRail, this._baseLine);
  }

  public getAllConnectedArticles(considerOnlyConfiguredArticles: boolean = false): ArticleContainer[] {
    return this.getConnectedArticlesFromLine(this._baseLine, null, considerOnlyConfiguredArticles);
  }

  public getArticleLineByGuid(articleGuid: string, useCache: boolean = true): AdvancedLine {
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleGuid);
    return !articleContainer ? null : this.getArticleLine(articleContainer, useCache);
  }

  public getArticleLine(article: ArticleContainer, useCache: boolean = true): AdvancedLine {
    return this.articleLineHolder.getArticleLineByAcceptingUsage(article, this._acceptingAreaUsage, this._baseLine, useCache);
  }

  public getLeftArticleContainer(usageId: string, excludedTypeIds: number[] = null): ArticleContainer {
    const sortedArticleGuids: string[] = this.articleLineHolder.getSortedContainerGuids(this.acceptingArticleUsageId);
    return this.getLeftArticleContainerInSource(usageId, sortedArticleGuids, excludedTypeIds);
  }

  public getLeftArticleContainerByPoint(pointOnRail: Vector3, excludedTypeIds: number[] = null, excludedArticleGUIDs: string[] = null): ArticleContainer {
    const leftContainers: ArticleContainer[] = this.getConnectedArticlesFromLine(new AdvancedLine(this.startPoint, pointOnRail), excludedArticleGUIDs);
    if (leftContainers.length === 0) {
      return null;
    }
    const leftArticleContainer: ArticleContainer = leftContainers[leftContainers.length - 1];
    return !excludedTypeIds || !ArticleUtils.isArticleOfAnyTypeId(leftArticleContainer.article, excludedTypeIds)
      ? leftArticleContainer
      : this.getLeftArticleContainerInSource(
          leftArticleContainer.articleGUID,
          ArticleContainerUtils.extractArticleGUIDs(leftContainers),
          excludedTypeIds
        );
  }

  private getLeftArticleContainerInSource(usageId: string, sourceUsageIds: string[], excludedTypeIds: number[] = null): ArticleContainer {
    const usageIdIndex: number = sourceUsageIds.indexOf(usageId);
    if (usageIdIndex === -1 || usageIdIndex === 0) {
      return null;
    }
    const leftUsageId: string = sourceUsageIds[usageIdIndex - 1];
    const leftArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(leftUsageId);
    if (!leftArticleContainer) {
      return null;
    }
    return !excludedTypeIds || !ArticleUtils.isArticleOfAnyTypeId(leftArticleContainer.article, excludedTypeIds)
      ? leftArticleContainer
      : this.getLeftArticleContainerInSource(leftArticleContainer.articleGUID, sourceUsageIds, excludedTypeIds);
  }

  public getRightArticleContainer(usageId: string, excludedTypeIds: number[] = null): ArticleContainer {
    const sortedArticleGuids: string[] = this.articleLineHolder.getSortedContainerGuids(this.acceptingArticleUsageId);
    return this.getRightArticleContainerInSource(usageId, sortedArticleGuids, excludedTypeIds);
  }

  public getRightArticleContainerByPoint(pointOnRail: Vector3, excludedTypeIds: number[] = null, excludedArticleGUIDs: string[] = null): ArticleContainer {
    const rightContainers: ArticleContainer[] = this.getConnectedArticlesFromLine(new AdvancedLine(pointOnRail, this.endPoint), excludedArticleGUIDs);
    if (rightContainers.length === 0) {
      return null;
    }
    const rightArticleContainer: ArticleContainer = rightContainers[0];
    return !excludedTypeIds || !ArticleUtils.isArticleOfAnyTypeId(rightArticleContainer.article, excludedTypeIds)
      ? rightArticleContainer
      : this.getRightArticleContainerInSource(
          rightArticleContainer.articleGUID,
          ArticleContainerUtils.extractArticleGUIDs(rightContainers),
          excludedTypeIds
        );
  }

  private getRightArticleContainerInSource(usageId: string, sourceUsageIds: string[], excludedTypeIds: number[] = null): ArticleContainer {
    const usageIdIndex: number = sourceUsageIds.indexOf(usageId);
    if (usageIdIndex === -1 || usageIdIndex === sourceUsageIds.length - 1) {
      return null;
    }
    const rightUsageId: string = sourceUsageIds[usageIdIndex + 1];
    const rightArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(rightUsageId);
    if (!rightArticleContainer) {
      return null;
    }
    return !excludedTypeIds || !ArticleUtils.isArticleOfAnyTypeId(rightArticleContainer.article, excludedTypeIds)
      ? rightArticleContainer
      : this.getRightArticleContainerInSource(rightArticleContainer.articleGUID, sourceUsageIds, excludedTypeIds);
  }

  /**
   * Provides common line for all given articles. Start point of the question-result line
   * will be start point of the nearest article line to start of base line.
   * End point - end point of the nearest article line to end of base line.
   *
   * @return line or null if no article is placed on the base line
   */
  public calculateCommonArticlesLine(containers: ArticleContainer[]): AdvancedLine {
    let nearestStartLine: AdvancedLine = null;
    let nearestEndLine: AdvancedLine = null;

    for (const container of containers) {
      const articleLine: AdvancedLine = this.getArticleLine(container);
      if (articleLine) {
        if (!nearestStartLine) {
          nearestStartLine = articleLine;
          nearestEndLine = articleLine;
        } else {
          // search nearest point to start of baseLine
          if (articleLine.start.distanceTo(this._baseLine.start) < nearestStartLine.start.distanceTo(this._baseLine.start)) {
            nearestStartLine = articleLine;
          }
          // search nearest point to end of baseLine
          if (articleLine.end.distanceTo(this._baseLine.end) < nearestEndLine.end.distanceTo(this._baseLine.end)) {
            nearestEndLine = articleLine;
          }
        }
      }
    }

    if (!nearestStartLine) {
      return null;
    } else {
      return new AdvancedLine(
        nearestStartLine.start,
        nearestEndLine.end,
        new OverlapInfo(
          null,
          nearestStartLine.overlapLeft,
          nearestEndLine.overlapRight,
          nearestStartLine.overlapLeftTypes,
          nearestEndLine.overlapRightTypes
        )
      );
    }
  }

  public calculateCumulativeLengthsOfArticleLines(articles: ArticleContainer[]): number {
    let cumulativeLength: number = 0;
    const articleLines: Line[] = [];
    for (const combinedArticle of articles) {
      const articleLine: Line = this.getArticleLine(combinedArticle);
      if (articleLine) {
        articleLines.push(articleLine);
      }
    }
    if (!ArrayUtils.isEmpty(articleLines)) {
      const firstArticleLine: Line = articleLines[0];
      const lastArticleLine: Line = articleLines[articleLines.length - 1];
      cumulativeLength = firstArticleLine.start.distanceTo(lastArticleLine.end);
    }
    return cumulativeLength;
  }

  public getDistanceFromStart(point: Vector3): number {
    return this._baseLine.start.distanceTo(point);
  }

  public getDistanceFromConfiguredStart(point: Vector3): number {
    return this.configuredLine.start.distanceTo(point);
  }

  public getConfiguredStartOffset(): number {
    return this._baseLine.start.distanceTo(this.configuredLine.start);
  }

  /**
   * Calculates line on baseLine with given offset and length.
   */
  public calculateArticleLine(offset: number, length: number): AdvancedLine {
    const startPoint: Vector3 = this.calculatePointByOffset(offset);
    const endPoint: Vector3 = this.calculatePointByOffset(offset + length);
    return new AdvancedLine(startPoint, endPoint);
  }

  public calculatePointByOffset(offset: number): Vector3 {
    return MathUtils.getInterpolated(this._baseLine.end, this._baseLine.start, offset / this._baseLine.length);
  }

  public calculateArticleOffset(usageId: string, globalContainer: GlobalContainer): number {
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
    if (!articleContainer) {
      return NaN;
    }
    const articleLine: Line = this.getArticleLine(articleContainer);
    return !articleLine ? NaN : this.getDistanceFromStart(articleLine.start);
  }

  private getConnectedArticlesByAcceptingArea(
    acceptingAreaUsage: ConnectionAreaUsageVO,
    considerOnlyConfiguredArticles: boolean = false
  ): ArticleContainer[] {
    if (!acceptingAreaUsage) {
      return [];
    }
    const connectedArticles: ArticleContainer[] = this.containerService.getConnectedArticleContainersByAcceptingAreaId(acceptingAreaUsage.identity);
    if (!considerOnlyConfiguredArticles) {
      return connectedArticles;
    }
    const result: ArticleContainer[] = [];
    for (const articleContainer of connectedArticles) {
      if (this.isArticleConfigured(articleContainer.article)) {
        result.push(articleContainer);
      }
    }
    return result;
  }

  private findArticlesFromLine(commonLine: AdvancedLine, articles: ArticleContainer[], excludedArticleGUIDs: string[] = null): ArticleContainer[] {
    const articlesBetweenPoints: ArticleContainer[] = [];

    for (const article of articles) {
      if (!excludedArticleGUIDs || excludedArticleGUIDs.indexOf(article.articleGUID) === -1) {
        const figureLine: AdvancedLine = this.getArticleLine(article);
        if (figureLine) {
          let maxLeftOverlapping: number = 0;
          let maxRightOverlapping: number = 0;
          if (this.getDistanceFromStart(figureLine.start) > this.getDistanceFromStart(commonLine.start)) {
            maxLeftOverlapping = AdvancedLine.calculateMaxOverlapLength(commonLine, figureLine);
          } else {
            maxRightOverlapping = AdvancedLine.calculateMaxOverlapLength(figureLine, commonLine);
          }

          if (
            !MathUtils.equalsNumber3D(figureLine.end, commonLine.start, maxRightOverlapping + CommonConstants.EPSILON) &&
            !MathUtils.equalsNumber3D(figureLine.start, commonLine.end, maxLeftOverlapping + CommonConstants.EPSILON) &&
            commonLine.hasLine(figureLine, false)
          ) {
            articlesBetweenPoints.push(article);
          }
        }
      }
    }

    return articlesBetweenPoints;
  }

  private isArticleConfigured(article: ArticleVO): boolean {
    return ArticleUtils.isArticleConfigured(article);
  }

  private sortArticlesByLine(articles: ArticleContainer[], commonLine: Line, reverseSort: boolean = false): ArticleContainer[] {
    const sortedArticles: ArticleContainer[] = [];
    if (this.articleLineHolder.cacheEnabled) {
      // force to create cache for given articles
      const articleIdMapping: Dictionary<string, ArticleContainer> = new Dictionary<string, ArticleContainer>();
      for (const article of articles) {
        this.getArticleLine(article);
        articleIdMapping.setValue(article.articleGUID, article);
      }
      for (const sortedGuid of this.articleLineHolder.getSortedContainerGuids(this.acceptingArticleUsageId)) {
        const article: ArticleContainer = articleIdMapping.getValue(sortedGuid);
        if (article) {
          sortedArticles.push(article);
        }
      }
    } else {
      let articleAdded: boolean = false;
      for (const article of articles) {
        articleAdded = false;
        if (sortedArticles.length === 0) {
          sortedArticles.push(article);
        } else {
          const articleLine: Line = this.getArticleLine(article);
          for (const sortedArticle of sortedArticles) {
            const sortedArticleLine: Line = this.getArticleLine(sortedArticle);
            if (articleLine.start.distanceTo(commonLine.start) < sortedArticleLine.start.distanceTo(commonLine.start)) {
              sortedArticles.splice(sortedArticles.indexOf(sortedArticle), 0, article);
              articleAdded = true;
              break;
            }
          }

          if (!articleAdded) {
            sortedArticles.push(article);
          }
        }
      }
    }
    if (reverseSort) {
      return sortedArticles.reverse();
    } else {
      return sortedArticles;
    }
  }

  public calculateOffsetToLastArticle(): number {
    const connectedArticles: ArticleContainer[] = this.getAllConnectedArticles();
    let articleOffset: number = this.getConfiguredStartOffset();
    if (connectedArticles.length > 0) {
      const lastConnectedArticleLine: AdvancedLine = this.getArticleLine(connectedArticles[connectedArticles.length - 1]);
      articleOffset += this.getDistanceFromConfiguredStart(lastConnectedArticleLine.end);
    }
    return articleOffset;
  }

  /**
   * Method emulates moving articles to create free space to insert article on the mounting rail.
   * <b>Note:</b> use this method to check if insert operation is available (enough free space).
   *
   * @param sortedArticles sorted articles on rail (from start to end of rail) that need to be checked
   * @param articleLine line that new article will occupy on rail
   * @param removeGapBetweenArticles force to remove gap between articles
   * @param moveLeft force to move articles left to free space
   * @param forceUnmoveableArticles move even unmovable articles
   * @param ignoreBaseLineOutOfBoundsCheck ignore the case when article does not fit base line
   * @param removeAllGaps force to remove gaps between articles in some rare cases
   * @param excludedArticleGUIDs array article GUIDs that should be excluded
   * @param maxAllowedMoveOffsetMap map where key is article GUID that has movement restrictions due to attached accessories,
   * value is max offset in 3d-space by which article container can be moved
   *
   * @return map where key is article GUID that needs to be moved, value is offset in 3d-space by which article container needs to be moved.
   *       If free space is not enough to insert article then null is returned.
   */
  private calculateOffsetToMoveArticles(
    sortedArticles: ArticleContainer[],
    articleLine: AdvancedLine,
    removeGapBetweenArticles: boolean = false,
    moveLeft: boolean = false,
    forceUnmoveableArticles: boolean = false,
    ignoreBaseLineOutOfBoundsCheck: boolean = false,
    removeAllGaps: boolean = false,
    excludedArticleGUIDs: string[] = null,
    maxAllowedMoveOffsetMap: Dictionary<string, number> = null
  ): Dictionary<string, Vector3> {
    if (ArrayUtils.isEmpty(sortedArticles)) {
      return new Dictionary<string, Vector3>();
    }
    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    const processedArticleGUIDs: string[] = []; // articles that already have been moved
    let targetLine: AdvancedLine = articleLine.clone();
    if (moveLeft) {
      targetLine.reverse();
    }
    let previousArticleLine: Line = null;
    for (const sortedArticle of sortedArticles) {
      const sortedArticleLine: AdvancedLine = this.getArticleLine(sortedArticle, !moveLeft);

      if (moveLeft) {
        // reverse only valid in case when sortedArticleLine is not from cache
        sortedArticleLine.reverse();
      }
      if (previousArticleLine && removeGapBetweenArticles && !removeAllGaps) {
        removeGapBetweenArticles = MathUtils.equalsNumber3D(previousArticleLine.end, sortedArticleLine.start);
      }
      // if article has not been moved yet
      if (processedArticleGUIDs.indexOf(sortedArticle.articleGUID) === -1) {
        // TODO: (from flex) SMAR-40: it should be reviewed because such comparison have sense only for non overlapped articles
        if (MathUtils.equalsNumber3D(targetLine.end, sortedArticleLine.start)) {
          return moveMap;
        }
        // if move line intersects target line, then move article
        if (targetLine.hasLine(sortedArticleLine, false) || sortedArticleLine.hasLine(targetLine, false) || removeGapBetweenArticles) {
          if (!forceUnmoveableArticles && !ArticleUtils.isArticleMovable(sortedArticle.article)) {
            return null;
          }

          // create line for all articles that are connected together
          const moveArticles: ArticleContainer[] = this.articleContainerUtils.findCombinedArticles(sortedArticle, false, excludedArticleGUIDs);
          const moveLine: AdvancedLine = this.calculateCommonArticlesLine(moveArticles);
          if (moveLeft) {
            moveLine.reverse();
          }
          // if article placed inside combined article - couldn't move combined articles
          if (moveArticles.length > 1 && !MathUtils.equalsNumber3D(targetLine.start, moveLine.start) && moveLine.hasPoint(targetLine.start)) {
            return null;
          }

          const overlapLength: number = AdvancedLine.calculateMaxOverlapLength(targetLine, moveLine);
          if (overlapLength > targetLine.end.distanceTo(moveLine.start)) {
            return moveMap;
          }

          const overlappedStartPoint: Vector3 = MathUtils.getInterpolated(
            moveLine.start,
            moveLine.end,
            (moveLine.length - overlapLength) / moveLine.length
          );
          const offsetInSpace: Vector3 = targetLine.end.clone().sub(overlappedStartPoint);
          for (const moveArticle of moveArticles) {
            const maxAllowedOffset: number = ReplaceBusbarUtils.getMaxAllowedMoveOffset(maxAllowedMoveOffsetMap, [moveArticle]);
            if (maxAllowedOffset < offsetInSpace.x) {
              offsetInSpace.setX(maxAllowedOffset);
            }
            moveMap.setValue(moveArticle.articleGUID, offsetInSpace);
            processedArticleGUIDs.push(moveArticle.articleGUID);
          }
        } else {
          // don't need to check other articles anymore
          return moveMap;
        }
      }

      // append line of the moved article to target line
      const offsetFromMap: Vector3 = moveMap.getValue(sortedArticle.articleGUID);
      const targetEndPoint: Vector3 = sortedArticleLine.end.clone().add(offsetFromMap);
      targetLine = new AdvancedLine(
        targetLine.start,
        targetEndPoint,
        new OverlapInfo(
          null,
          targetLine.overlapLeft,
          sortedArticleLine.overlapRight,
          targetLine.overlapLeftTypes,
          sortedArticleLine.overlapRightTypes
        )
      );
      previousArticleLine = sortedArticleLine;

      // moved articles outside the base line
      if (!ignoreBaseLineOutOfBoundsCheck && !this._baseLine.hasPoint(targetLine.end)) {
        return null;
      }
    }

    return moveMap;
  }

  public createConnectionOnLine(freeLine: Line, acceptingArea: ConnectionAreaVO): Connection {
    const baseRectangle: Rectangle3D = this._acceptingConnection.localRectangle;
    const connectionRectangle: Rectangle3D = this.containerService.convertLineToRectangle(freeLine, baseRectangle);

    const mesh: Mesh = MeshUtils.cloneMesh(this._acceptingConnection.mesh);
    mesh.name = StringUtils.createUuid();
    const articleContainer: ArticleContainer = this._acceptingConnection.articleContainer;
    const connection: Connection = this.connectionFactory.getNewInstance(articleContainer, mesh);
    articleContainer.setConnection(connection);
    articleContainer.addChildToModel(mesh);
    const connectorPoint: Vector3 = MathUtils.calculateConnectorFromRect(connectionRectangle);
    connection.metaElementVO = acceptingArea;
    connection.metaElementUsage = this._acceptingAreaUsage;
    connection.mesh.position.copy(connectorPoint);

    return connection;
  }

  public getFreeLengthByLine(line: AdvancedLine): number {
    if (!this.baseLine.hasLine(line)) {
      return 0;
    }
    let freeLength: number = line.length;
    const articlesOnLine: ArticleContainer[] = this.getConnectedArticlesFromLine(line);
    for (const article of articlesOnLine) {
      const articleLine: AdvancedLine = this.getArticleLine(article);
      if (line.hasLine(articleLine)) {
        freeLength -= articleLine.length;
      } else if (line.hasPoint(articleLine.start)) {
        freeLength -= line.end.distanceTo(articleLine.start);
      } else if (line.hasPoint(articleLine.end)) {
        freeLength -= line.start.distanceTo(articleLine.end);
      }
    }
    return freeLength > 0 ? freeLength : 0;
  }
}
